import i18next from 'i18next'
import { DateTime } from 'luxon'
import ordinal from 'ordinal'
import { sprintf } from 'sprintf-js'
import { ScaleLabeling } from '../types/charts'
import { Currency } from '../types/models'

/**
 * Localization utilities.
 */
export const lang = {

  /**
   * Get the current language.
   */
  current() {
    return i18next.language
  },

  /**
   * Localize number.
   */
  num(n: number | null, decimals: number | undefined = undefined, unit: string | undefined = undefined): string {
    let s = ''
    if (n === null || isNaN(n)) {
      s = '-'
      if (decimals !== undefined) {
        s += '.'
        s += '--------------------------------'.substring(0, decimals)
      }
    } else {
      if (decimals !== undefined) {
        s = sprintf(`%.${decimals}f`, n)
      } else {
        s = `${n}`
      }
    }

    if (unit !== undefined) {
      s += '\u00A0' + unit
    }

    switch (i18next.language) {
      case 'fi':
        s = s.replace('.', ',')
        break
    }

    return s
  },

  /**
   * Localize money.
   */
  money(n: number, currency: Currency, digits = 2) {
    switch (currency) {
      case 'EUR':
        return `${lang.num(n, digits)}\u00A0€`
    }
  },

  moneyShortUnit(currency: Currency) {
    switch (currency) {
      case 'EUR':
        return 'c'
      default:
        return '?'
    }
  },

  moneyLongUnit(currency: Currency) {
    const lang = i18next.language
    switch (currency) {
      case 'EUR':
        if (lang === 'fi') return 'snt'
        return 'cent(s)'
      default:
        return '?'
    }
  },

  /**
   * Localize money in the smaller unit.
   */
  moneyShort(n: number, currency: Currency) {
    switch (currency) {
      case 'EUR':
        return `${n}${lang.moneyShortUnit(currency)}`
      default:
        return `${n}${lang.moneyShortUnit(currency)}`
    }
  },

  /**
   * Localize a date.
   */
  date(day: DateTime): string {
    return Intl.DateTimeFormat(lang.current()).format(day.toJSDate())
  },

  /**
   * Localize a hour and minute.
   */
  time(day: DateTime): string {
    return Intl.DateTimeFormat(lang.current(), { hour: 'numeric', minute: 'numeric' }).format(day.toJSDate())
  },

  /**
   * Localize a date from timestamp.
   */
  dateFromStamp(timestamp: number): string {
    return lang.date(DateTime.fromMillis(timestamp))
  },

  /**
   * Localize a hour and minute from timestamp.
   */
  timeFromStamp(timestamp: number): string {
    return lang.time(DateTime.fromMillis(timestamp))
  },

  /**
   * Localize a date without year.
   */
  dateShort(day: DateTime): string {
    const year = `${day.year}`
    return lang.date(day).replace(year, '').replace(/^[^0-9]+/, '').replace(/[^0-9]+$/, '')
  },

  /**
   * Localized day name per order number or string '1'..'7'.
   */
  dayNameShort(n: string | number): string {
    const lang = i18next.language

    if (lang === 'fi') {
      switch (`${n}`) {
        case '1': return 'Ma'
        case '2': return 'Ti'
        case '3': return 'Ke'
        case '4': return 'To'
        case '5': return 'Pe'
        case '6': return 'La'
        case '7': return 'Su'
        default: return '??'
      }
    } else {
      switch (`${n}`) {
        case '1': return 'Mon'
        case '2': return 'Tue'
        case '3': return 'Wed'
        case '4': return 'Thu'
        case '5': return 'Fri'
        case '6': return 'Sat'
        case '7': return 'Sun'
        default: return '???'
      }
    }
  },

  /**
   * Get the long version of weekday.
   */
  dayNameLong(n: string | number): string {
    const lang = i18next.language

    if (lang === 'fi') {
      switch (`${n}`) {
        case '1': return 'Maanantai'
        case '2': return 'Tiistai'
        case '3': return 'Keskiviikko'
        case '4': return 'Torstai'
        case '5': return 'Perjantai'
        case '6': return 'Lauantai'
        case '7': return 'Sunnuntai'
        default: return '??'
      }
    } else {
      switch (`${n}`) {
        case '1': return 'Monday'
        case '2': return 'Tuesday'
        case '3': return 'Wednesday'
        case '4': return 'Thursday'
        case '5': return 'Friday'
        case '6': return 'Saturday'
        case '7': return 'Sunday'
        default: return '???'
      }
    }
  },

  /**
   * Get single letters for each day.
   */
  dayLetters(): [string, string, string, string, string, string, string] {
    const lang = i18next.language

    switch (i18next.language) {
      case 'fi':
        return ['M', 'T', 'K', 'T', 'P', 'L', 'S']
      case 'en':
        return ['M', 'T', 'W', 'T', 'F', 'S', 'S']
      default:
        throw new Error(`Day letters not supported for language '${lang}'.`)
    }
  },

  /**
   * Localize ordinal number.
   */
  ordinal(n: number): string {
    switch (i18next.language) {
      case 'fi':
        return `${n}.`
      case 'en':
        return `${ordinal(n)}`
      default:
        return `${n}.`
    }
  },

  /**
   * Construct long name of the weekday and its ordinal.
   */
  dayNameAndNumber(date: DateTime): string {
    return lang.dayNameLong(date.weekday) + ' ' + lang.ordinal(date.day)
  },

  /**
   * Localized month name per order number string '1'..'12'.
   */
  monthNameShort(n: number): string {
    const lang = i18next.language

    if (lang === 'fi') {
      switch (n) {
        case 1: return 'Tam'
        case 2: return 'Hel'
        case 3: return 'Maa'
        case 4: return 'Huh'
        case 5: return 'Tou'
        case 6: return 'Kes'
        case 7: return 'Hei'
        case 8: return 'Elo'
        case 9: return 'Syy'
        case 10: return 'Lok'
        case 11: return 'Mar'
        case 12: return 'Jou'
        default: return '???'
      }
    } else {
      switch (n) {
        case 1: return 'Jan'
        case 2: return 'Feb'
        case 3: return 'Mar'
        case 4: return 'Apr'
        case 5: return 'May'
        case 6: return 'Jun'
        case 7: return 'Jul'
        case 8: return 'Aug'
        case 9: return 'Sep'
        case 10: return 'Oct'
        case 11: return 'Nov'
        case 12: return 'Dec'
        default: return '???'
      }
    }
  },

  dayAndMonth(date: DateTime): string {
    switch (i18next.language) {
      case 'fi':
        return `${lang.ordinal(date.day)} ${lang.monthNameShort(date.month)}`
      case 'en':
        return `${lang.monthNameShort(date.month)} ${lang.ordinal(date.day)}`
      default:
        return `${lang.ordinal(date.day)} ${lang.monthNameShort(date.month)}`
    }
  },

  week(n: number): string {
    switch (i18next.language) {
      case 'fi':
        return `Viikko ${n}`
      case 'en':
        return `Week ${n}`
      default:
        return `${n}`
    }
  },

  /**
   * Construct a string presenting the time stamp in the given scale.
   */
  timeLabel(stamp: number, scale: ScaleLabeling): string {
    const date = DateTime.fromMillis(stamp)
    switch (scale) {
      case 'minute':
        return lang.timeFromStamp(stamp) + ' | ' + lang.dayAndMonth(date)
      case 'quarter':
        return lang.timeFromStamp(stamp) + ' | ' + lang.dayAndMonth(date)
      case 'hour':
        return lang.timeFromStamp(stamp) + ' | ' + lang.dayAndMonth(date)
      case 'day':
        return lang.dayAndMonth(date)
      case 'week':
        return lang.week(date.weekNumber)
      case 'month':
        return lang.monthNameShort(date.month) + ` ${date.year}`
      case 'year':
        return `${date.year}`
      default:
        throw new Error(`Time scale '${scale}' not supported for label construction timeLabel().`)
    }
  },

  /**
   * Convert a month name to a number.
   */
  monthNumberFromString(str: string): string {
    return new Date(`${str} 01 2000`).toLocaleDateString('en', { month: '2-digit' })
  }
}
